import { useEffect } from 'react';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { applyChatsNotificationsRead } from './chats';
import { queryClient, queryKeys } from './query-client';
import { refreshUnreadNotifications } from './unread-notifications-store';

/**
 * Reading a chat clears its bell notifications — on every surface, by any route into the chat.
 *
 * Before this, the only way to clear a chat's notification was to click the notification itself: opening
 * the same chat from the sidebar, a deep link or the home screen left the bell lit on web AND on both
 * phones, because the bell feed is one feed. The server half is `POST /api/notifications
 * { status: 'READ', conversationId }` — the same scan the native apps reach through the serverless
 * bridge (`conversation-notifications-read`), so the three surfaces clear the same rows.
 *
 * Deliberately independent of the Chats read protocol (`/api/chat-reads`): that path needs a server-issued
 * `readThroughToken` that no window carries yet, so on its own it clears nothing.
 */

/** A chat open, the visibility flip and the first rendered message all land within a moment of each other. */
const COALESCE_MS = 2_000;
const lastSent = new Map<string, number>();

/** Test seam: the coalescing memory is module state that `jest.clearAllMocks()` cannot reach. */
export function resetConversationNotificationReads(): void {
  lastSent.clear();
}

/**
 * Mark `conversationId`'s unread bell rows read, then re-read the bell. Fire-and-forget: a failure leaves
 * the badge as it was, which is where it was before this existed. Returns whether a request was sent.
 */
export async function markConversationNotificationsRead(conversationId: string, now: number = Date.now()): Promise<boolean> {
  if (!conversationId) return false;
  const previous = lastSent.get(conversationId);
  if (previous !== undefined && now - previous < COALESCE_MS) return false;
  lastSent.set(conversationId, now);
  try {
    const response = await apiFetch(`${API_BASE}/api/notifications`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'READ', conversationId }),
    });
    const result = response.ok ? ((await response.json().catch(() => null)) as { ok?: boolean; marked?: number } | null) : null;
    if (result?.ok) {
      // The Chats row's dot carries this chat's notification state too; clear it with the bell, not on the
      // list's next poll. Even with nothing marked here, the row may still show a state another surface cleared.
      await applyChatsNotificationsRead(conversationId);
      if ((result.marked ?? 0) > 0) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
        refreshUnreadNotifications();
      }
    }
  } catch {
    // Best effort, like every other bell read on this surface.
  }
  return true;
}

/**
 * While `conversationId` is on screen and the tab is visible: clear its notifications on entry and again
 * whenever a new message lands (`latestMessageKey`); and once more on LEAVING the chat — a row created
 * while the reader was looking at it has been read too. Every mark is gated on the tab being visible: a
 * message that lands in a background tab has not been seen, so its notification must stay.
 */
export function useConversationNotificationsRead(conversationId: string | null, visible: boolean, latestMessageKey: string | null): void {
  useEffect(() => {
    if (!conversationId || !visible) return;
    const markIfVisible = () => {
      if (document.visibilityState === 'visible') void markConversationNotificationsRead(conversationId);
    };
    markIfVisible();
    document.addEventListener('visibilitychange', markIfVisible);
    return () => document.removeEventListener('visibilitychange', markIfVisible);
  }, [conversationId, visible, latestMessageKey]);

  // The leave read: only a real teardown (the chat closed, or another conversation opened), never a
  // message landing — and only while the reader can see the tab.
  useEffect(() => {
    if (!conversationId || !visible) return;
    return () => {
      // Forget the stamp either way: a hidden leave sends nothing, and the next visible entry must not be
      // coalesced against an entry the reader has already left.
      lastSent.delete(conversationId);
      if (document.visibilityState !== 'visible') return;
      void markConversationNotificationsRead(conversationId);
    };
  }, [conversationId, visible]);
}
